/**
 * The signed-in person's engagement record through Paige's API (`/v1/me/engagement`, the contract's
 * Engagement; docs/engineering/engagement.md). The company is the `Paige-Tenant` header; every
 * change answers the record as it now stands.
 */
export interface PaigeEngagement {
  milestones: {
    firstVisit: string;
    firstJob: string | null;
    firstConfirmation: string | null;
    firstExport: string | null;
  };
  tour: { offeredAt: string | null; takenAt: string | null };
  hints: { show: boolean; closed: { id: string; closedAt: string }[] };
  tips: {
    state: 'unasked' | 'opted-in' | 'declined' | 'skipped';
    name: string | null;
    email: string | null;
    decidedAt: string | null;
    source: 'in-app' | 'settings' | 'unsubscribe' | null;
  };
  lastActiveDay: string;
}

export type PaigeEngagementMilestone = keyof PaigeEngagement['milestones'];

export type PaigeConsentChange =
  { choice: 'opt-in'; name: string; email: string } | { choice: 'decline' } | { choice: 'skip' };

export interface PaigeEngagementChannel {
  read: () => Promise<PaigeEngagement>;
  note: (milestone: PaigeEngagementMilestone, at?: string) => Promise<PaigeEngagement>;
  tour: (step: 'offered' | 'taken', at?: string) => Promise<PaigeEngagement>;
  hints: (show: boolean, closed: string[]) => Promise<PaigeEngagement>;
  consent: (change: PaigeConsentChange) => Promise<PaigeEngagement>;
}

/** The record under `{api}/me/engagement`, `api` being Paige's `/v1`. */
export function engagementChannel(api: string, tenant: string | undefined): PaigeEngagementChannel {
  const send = (path: string, method: string, body?: unknown) =>
    answer(
      fetch(`${api}/me/engagement${path}`, {
        method,
        credentials: 'include',
        headers: {
          ...(tenant ? { 'Paige-Tenant': tenant } : {}),
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      }),
    );
  return {
    read: () => send('', 'GET'),
    note: (milestone, at) => send('/milestones', 'POST', { milestone, ...(at ? { at } : {}) }),
    tour: (step, at) => send('/tour', 'POST', { step, ...(at ? { at } : {}) }),
    hints: (show, closed) => send('/hints', 'PUT', { show, closed }),
    consent: (change) => send('/consent', 'PUT', { ...change, source: 'in-app' }),
  };
}

/** The record, or Paige's reason as the error (the dialog shows it as it is). */
async function answer(pending: Promise<Response>): Promise<PaigeEngagement> {
  const response = await pending;
  if (response.ok) return (await response.json()) as PaigeEngagement;
  let reason = 'That didn’t save. Try again.';
  try {
    const problem = (await response.json()) as { detail?: string; title?: string };
    reason = problem.detail ?? problem.title ?? reason;
  } catch {
    /* The fallback says it. */
  }
  throw new Error(reason);
}

/** Paige's API root from the adapter's options: the API's own, or the job channel's parent. */
export function apiRootOf(engineOrigin: string, apiOrigin: string | undefined): string {
  return apiOrigin ?? engineOrigin.replace(/\/jobs\/[^/]+$/, '');
}
